import { useState } from "react";
import { Clock, MapPin, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { ProPractBadge } from "./ProPractBadge";
import { SERVICES } from "./data";
import { useCatalog } from "@/lib/site";
import { RichText, stripHtml } from "./RichText";

const ServiceCard = ({ s, i, onBook }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isLong = stripHtml(s.desc).length > 180;
  return (
    <Reveal delay={i * 0.12}>
      <article
        data-testid={`service-card-${i}`}
        className="group flex h-full flex-col rounded-3xl border border-sand/10 bg-sand/[0.04] p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:bg-sand/[0.08] hover:shadow-deep"
      >
        <span className="font-display text-5xl font-medium text-sand/15 transition-colors duration-500 group-hover:text-gold/40">
          0{i + 1}
        </span>
        <h3 className="mt-6 font-display text-2xl font-semibold">{s.title}</h3>
        <div className="mt-3 flex-1">
          <RichText
            html={s.desc}
            className={`text-sm leading-relaxed text-sand/70 [&_h3]:mt-4 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-sand [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1 [&_p]:mt-2 [&_p:first-child]:mt-0 [&_strong]:font-semibold [&_strong]:text-sand ${!expanded && isLong ? "line-clamp-4" : ""}`}
          />
          {isLong && (
            <button
              data-testid={`service-desc-toggle-${i}`}
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 font-outfit text-xs font-semibold uppercase tracking-wider text-gold"
            >
              {expanded ? "Show less" : "Read more"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        <div className="mt-6 space-y-2 border-t border-sand/10 pt-5 text-xs font-outfit text-sand/60">
          <p className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-gold" /> {s.duration} · {s.price}
          </p>
          <p className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-gold" /> {s.mode}
          </p>
        </div>
        {(s.why || s.who) && (
          <div className="mt-4">
            <button
              data-testid={`service-why-toggle-${i}`}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 font-outfit text-xs font-semibold uppercase tracking-wider text-gold"
            >
              Why & who should book
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div data-testid={`service-why-panel-${i}`} className="mt-3 space-y-2 rounded-xl bg-sand/5 p-4 text-xs leading-relaxed text-sand/70">
                {s.why && <p><span className="font-semibold text-gold">Why: </span>{s.why}</p>}
                {s.who && <p><span className="font-semibold text-gold">Who: </span>{s.who}</p>}
              </div>
            )}
          </div>
        )}
        <button
          data-testid={`service-book-btn-${i}`}
          onClick={() => onBook(s)}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-sand px-5 py-3 font-outfit text-sm font-semibold text-ink transition-all duration-300 group-hover:bg-terra group-hover:text-white"
        >
          Book this session <ArrowUpRight className="h-4 w-4" />
        </button>
      </article>
    </Reveal>
  );
};

export const Services = ({ onBook }) => {
  const { services, propractLive } = useCatalog();
  return (
  <section id="services" data-testid="services-section" className="bg-plum py-20 text-sand sm:py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-outfit text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-gold">
            Clinical & holistic care
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
            Care for every stage, <br className="hidden sm:block" />
            <span className="italic text-gold">birth to weaning.</span>
          </h2>
        </div>
        <ProPractBadge dark />
      </Reveal>

      <div className={`mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 ${services.length === 2 ? "mx-auto lg:max-w-4xl" : "lg:grid-cols-3"}`}>
        {services.map((s, i) => (
          <ServiceCard key={s.id || i} s={s} i={i} onBook={onBook} />
        ))}
      </div>
    </div>
  </section>
  );
};
